import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ImagePlus, Loader2, LogOut, X } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/png"];
const PH_PHONE_RE = /^(\+639\d{9}|09\d{9})$/;

/**
 * CompleteProfilePage — locked screen for Google-auth users who still
 * need to submit PRC license number, PRC ID image, and PH phone.
 *
 * Backend contract:
 *   POST /api/auth/complete-profile (multipart)
 *     Form: prc_license_number, phone, prc_id_image
 *     → clears profile_incomplete flag, leaves is_verified=false so
 *       the account enters the standard admin verification queue.
 */
export default function CompleteProfilePage() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [prcLicense, setPrcLicense] = useState("");
  const [phone, setPhone] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const phoneValid = useMemo(
    () => PH_PHONE_RE.test(phone.replace(/\s+/g, "")),
    [phone]
  );

  const canSubmit =
    prcLicense.trim().length >= 4 &&
    phoneValid &&
    !!file &&
    !submitting;

  const normalizePhone = (v) => {
    const trimmed = v.replace(/\s+/g, "");
    if (trimmed.startsWith("09") && trimmed.length === 11) {
      return "+63" + trimmed.slice(1);
    }
    return trimmed;
  };

  const acceptFile = (f) => {
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setError("Please upload a JPG or PNG image of your PRC ID.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setError("PRC ID image must be 5 MB or smaller.");
      return;
    }
    setError("");
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("prc_license_number", prcLicense.trim());
      fd.append("phone", normalizePhone(phone));
      fd.append("prc_id_image", file);
      await api.post("/auth/complete-profile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshMe();
      navigate("/verification-pending", { replace: true });
    } catch (err) {
      const raw = formatApiError(err, "Couldn't submit your profile.");
      const map = {
        INVALID_PRC_LICENSE:
          "That PRC license number doesn't look right. Use letters, digits or dashes (4–20 chars).",
        INVALID_IMAGE_TYPE: "Please upload a JPG or PNG image.",
        FILE_TOO_LARGE: "PRC ID image must be 5 MB or smaller.",
        EMPTY_FILE: "Please attach your PRC ID image.",
        PROFILE_ALREADY_COMPLETE:
          "Your profile is already submitted. Refresh the page to continue.",
      };
      setError(map[raw] || raw);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mb-page min-h-screen">
      <header className="border-b border-gray-100 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
          <BrandLogo className="h-8" />
          <button
            type="button"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
            className="mb-focus-ring inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800"
            data-testid="complete-profile-logout"
          >
            <LogOut className="h-3.5 w-3.5" />
            Log out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-14">
        <div className="mb-rise">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-navy/15 bg-brand-navy-soft px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-navy">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
            One last step
          </div>
          <h1
            className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
            data-testid="complete-profile-headline"
          >
            Complete your broker profile.
          </h1>
          <p className="mt-3 max-w-lg text-base leading-relaxed text-gray-500">
            Signed in as{" "}
            <span className="font-semibold text-gray-800">
              {user?.email}
            </span>
            . Add your PRC license, phone, and ID image so our admin can
            verify you.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mt-10 rounded-2xl border border-gray-100 bg-white p-8 shadow-mb-card"
          data-testid="complete-profile-form"
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="cp-prc" className="text-gray-700">
                PRC license number
              </Label>
              <Input
                id="cp-prc"
                value={prcLicense}
                onChange={(e) => setPrcLicense(e.target.value)}
                placeholder="0012345"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="complete-profile-prc"
                autoComplete="off"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-phone" className="text-gray-700">
                Mobile number (+63)
              </Label>
              <Input
                id="cp-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+639171234567"
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
                data-testid="complete-profile-phone"
                aria-invalid={phone.length > 0 && !phoneValid}
                required
              />
              {phone.length > 0 && !phoneValid && (
                <p
                  className="text-xs font-medium text-red-600"
                  data-testid="complete-profile-phone-error"
                >
                  Use a Philippine mobile — +639… or 09… (11 digits).
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <Label className="text-gray-700">PRC ID image</Label>
            <label
              className={`mt-2 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition ${
                dragging
                  ? "border-brand-navy bg-brand-navy-soft"
                  : "border-gray-200 bg-gray-50 hover:border-brand-navy/50"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                acceptFile(e.dataTransfer.files?.[0]);
              }}
              data-testid="complete-profile-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={(e) => acceptFile(e.target.files?.[0])}
                className="hidden"
                data-testid="complete-profile-file-input"
              />
              {!preview ? (
                <div className="flex flex-col items-center gap-2 text-sm text-gray-500">
                  <ImagePlus className="h-6 w-6 text-gray-400" />
                  <span className="font-medium text-gray-700">
                    Drop your PRC ID here, or click to upload
                  </span>
                  <span className="text-xs text-gray-400">
                    JPG or PNG · max 5 MB
                  </span>
                </div>
              ) : (
                <div className="relative w-full max-w-xs">
                  <img
                    src={preview}
                    alt="PRC preview"
                    className="w-full rounded-lg border border-gray-200"
                    data-testid="complete-profile-file-preview"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      clearFile();
                    }}
                    className="mb-focus-ring absolute -right-3 -top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm hover:text-gray-800"
                    data-testid="complete-profile-file-clear"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </label>
          </div>

          {error && (
            <div
              className="mt-6 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
              data-testid="complete-profile-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={!canSubmit}
            className="mb-focus-ring mt-8 h-12 w-full rounded-xl bg-[#1B2B4B] text-base font-semibold text-white shadow-sm hover:bg-[#14213A] disabled:opacity-60"
            data-testid="complete-profile-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Submit for verification
              </>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
